import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "counter", "instruction", "autoAdvance", "finish"]
  static outlets = ["cooking-timer", "servings-scaler"]
  static values = { steps: Array, index: { type: Number, default: 0 } }

  open() {
    this.indexValue = 0
    this.hideFinish()
    this.syncQuantities()
    this.show()
    this.overlayTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    this.keyHandler = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); this.next() }
      else if (e.key === "ArrowLeft") { e.preventDefault(); this.prev() }
      else if (e.key === "Escape") this.close()
    }
    document.addEventListener("keydown", this.keyHandler)

    this.setupSwipe()

    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then(lock => { this.wakeLock = lock }).catch(() => {})
    }
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.cleanup()
    this.dispatch("closed")
  }

  disconnect() {
    this.cleanup()
  }

  cleanup() {
    if (this.keyHandler) {
      document.removeEventListener("keydown", this.keyHandler)
      this.keyHandler = null
    }
    this.teardownSwipe()
    if (this.wakeLock) { this.wakeLock.release(); this.wakeLock = null }
  }

  setupSwipe() {
    this.touchStartX = 0
    this.touchStartY = 0
    this.onTouchStart = (e) => {
      this.touchStartX = e.changedTouches[0].screenX
      this.touchStartY = e.changedTouches[0].screenY
    }
    this.onTouchEnd = (e) => {
      const dx = e.changedTouches[0].screenX - this.touchStartX
      const dy = e.changedTouches[0].screenY - this.touchStartY
      if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return
      if (dx < 0) this.next()
      else this.prev()
    }
    this.overlayTarget.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.overlayTarget.addEventListener("touchend", this.onTouchEnd, { passive: true })
  }

  teardownSwipe() {
    if (this.onTouchStart) {
      this.overlayTarget.removeEventListener("touchstart", this.onTouchStart)
      this.overlayTarget.removeEventListener("touchend", this.onTouchEnd)
      this.onTouchStart = null
      this.onTouchEnd = null
    }
  }

  next() {
    if (this.indexValue < this.stepsValue.length - 1) {
      this.indexValue++
      this.show()
    } else {
      // Advancing past the last step ends the cook — show the finish card so
      // the flow doesn't dead-end into a hidden overlay.
      this.finish()
    }
  }

  finish() {
    if (this.hasFinishTarget) this.finishTarget.classList.remove("hidden")
  }

  hideFinish() {
    if (this.hasFinishTarget) this.finishTarget.classList.add("hidden")
  }

  // Hand off to the existing rating widget on the recipe page (it lives outside
  // this controller's scope, so reach it by id rather than a Stimulus target).
  rate() {
    this.close()
    // Honor reduced-motion: the CSS scroll-behavior guard doesn't cover the JS
    // smooth-scroll option, so gate it here.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    document.getElementById("rating_widget")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" })
  }

  prev() {
    if (this.indexValue > 0) {
      this.indexValue--
      this.show()
    }
  }

  onTimerFinished() {
    if (this.hasAutoAdvanceTarget && this.autoAdvanceTarget.checked) {
      setTimeout(() => this.next(), 2000)
    }
  }

  // The recipe page's meal-size scaler lives in <main>; cook mode renders at
  // body level (to be full-screen), so its always-visible ingredient list can't
  // be a scaler target. Copy the scaler's current quantities in on open.
  syncQuantities() {
    if (!this.hasServingsScalerOutlet) return
    const scaler = this.servingsScalerOutlet
    const dst = this.element.querySelectorAll("[data-cook-quantity]")
    scaler.quantityTargets.forEach((q, i) => { if (dst[i]) dst[i].textContent = q.textContent })
    const servings = scaler.hasServingsTarget ? scaler.servingsTargets[0]?.textContent : null
    if (servings) this.element.querySelectorAll("[data-cook-servings]").forEach((el) => { el.textContent = servings })
  }

  show() {
    const text = this.stepsValue[this.indexValue]
    this.instructionTarget.textContent = text
    this.counterTarget.textContent = `Step ${this.indexValue + 1} of ${this.stepsValue.length}`

    const progress = ((this.indexValue + 1) / this.stepsValue.length) * 100
    this.element.querySelector("[data-progress]").style.width = `${progress}%`

    this.updateTimer(text)
  }

  // Seed the timer's default duration for this step: the time named in the step
  // ("boil 2 minutes" → 2:00), else a 5:00 default the cook can adjust.
  updateTimer(text) {
    if (!this.hasCookingTimerOutlet) return

    const match = text.match(/(\d+)\s*(minute|min|hour|hr|second|sec)s?/i)
    let seconds = 300
    if (match) {
      seconds = parseInt(match[1], 10)
      const unit = match[2].toLowerCase()
      if (unit.startsWith("hour") || unit.startsWith("hr")) seconds *= 3600
      else if (unit.startsWith("min")) seconds *= 60
    }
    this.cookingTimerOutlet.setDuration(seconds)
  }
}
